import type React from 'react';
import { type FC, type PropsWithChildren, useRef, useState, useEffect } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import { isEllipsisActive } from './utils/isEllipsisActive';
import { TypographySize } from '../typography/enums/size';
import { StyledDisabledLink, StyledLink } from './components/StyledLink';
import { StyledExternalLink } from './components/StyledExternalLink';
import { Tooltip } from '../tooltip/Tooltip';

export interface LinkProps extends Pick<RouterLinkProps, 'to' | 'style'>, React.RefAttributes<HTMLAnchorElement> {
  size?: TypographySize.Large | TypographySize.Medium | TypographySize.Small;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  /**
   * Requires this component to be placed inside flex container to work properly.
   * @default false
   */
  ellipsis?: boolean;
  /**
   * Selector for cypress tests.
   */
  'data-cy'?: string;
  isExternal?: boolean;
  externalLinkOptions?: {
    rel: string;
    target: string;
  };
  ref?: React.Ref<HTMLAnchorElement>;
}

export const Link: FC<PropsWithChildren<LinkProps>> = ({
  children,
  ellipsis,
  size = TypographySize.Medium,
  disabled,
  onClick,
  isExternal,
  externalLinkOptions,
  to,
  ...restProps
}) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const [isTooltipEnabled, setIsTooltipEnabled] = useState(false);

  useEffect(() => {
    if (!(linkRef.current && ellipsis)) return;

    setIsTooltipEnabled(isEllipsisActive(linkRef.current));
  }, [ellipsis]);

  const link = isExternal ? (
    <StyledExternalLink ref={linkRef} href={to as string} size={size} {...externalLinkOptions} ellipsis={ellipsis}>
      {children}
    </StyledExternalLink>
  ) : (
    <StyledLink ref={linkRef} size={size} to={to} {...restProps} ellipsis={ellipsis} onClick={onClick}>
      {children}
    </StyledLink>
  );

  if (disabled) {
    return (
      <StyledDisabledLink size={size} to={to} {...restProps}>
        {children}
      </StyledDisabledLink>
    );
  }

  return isTooltipEnabled ? (
    <Tooltip arrow title={children}>
      {link}
    </Tooltip>
  ) : (
    link
  );
};
